const {Component} = require("react");
const PropTypes = require("prop-types");
const {classList} = require("helpers/html");
const {arrayGroup} = require("helpers/array");
const moment = require("moment");
const router = require("services/router");
const userStore = require("stores/UserStore");
const {Message, isRead, isSent} = require("components/backend/chat/Message");
const {debounce} = require("helpers/throttle");

class ChatWindow extends Component {

	static get propTypes() {
		return {
			// Data props
			channel: PropTypes.object.isRequired,
			messages: PropTypes.array.isRequired,

			// Callback props
			handleRead: PropTypes.func,
			handleMessageAction: PropTypes.func,
			handleScrollToTop: PropTypes.func,

			// State props
			active: PropTypes.bool,

			// Configuration props
			defaultAvatarUrl: PropTypes.string
		};
	}

	static get defaultProps() {
		return {
			active: false,
			defaultAvatarUrl: "img/avatar-placeholder.png"
		}
	}

	constructor(props) {
		super(props);

		// Flag to indicate whether we need to scroll to the last message
		this.receivedNewMessage = false;
	}

	componentWillReceiveProps(props) {
		if (props.messages.length > this.props.messages.length) {

			let currentLast = this.props.messages[this.props.messages.length -1];
			let newLast = props.messages[props.messages.length -1];

			if (!currentLast) {
				this.receivedNewMessage = true;
				return;
			}

			if (moment(newLast.created_at).isAfter(moment(currentLast.created_at))) {
				this.receivedNewMessage = true;
			}
		}
	}

	componentDidMount() {
		this.scrollToLastMessage("instant");

		// Set all messages in the channel as read
		if (this.props.active) {
			this.readUnreadMessages();
		}
	}

	componentDidUpdate() {

		// Scroll to the last new message
		if (this.receivedNewMessage) {
			this.receivedNewMessage = false;
			this.scrollToLastMessage();
		}

		// Set all messages in the channel as read
		if (this.props.active) {
			this.readUnreadMessages();
		}
	}

	/////////////////////////////////
	// Accessors
	/////////////////////////////////

	get className() {
		return classList(
			"message-area",
			this.props.active && "active"
		);
	}

	get groups() {
		return arrayGroup(this.props.messages, message => moment(message.created_at).format("YYYY-MM-DD HH"));
	}

	/////////////////////////////////
	// Helpers
	/////////////////////////////////

	getDateBarContent(date) {
		const now = moment();

		if (date.isSame(now, "d")) {
			return `I dag ${date.format("HH:mm")}`;
		}

		if (date.diff(now, "days") < 6) {
			return date.format("dddd HH:mm");
		}

		return date.format("YYYY-MM-DD HH:mm");
	}

	groupMessagesBySender(messages) {
		return messages.reduce((acc, message) => {
			let group = acc.length > 0 ? acc[acc.length - 1] : null;

			// Create new group
			if ((group == undefined) || (group.sender.id !== message.sender.id)) {
				group = {
					sender: message.sender,
					messages: []
				};

				acc.push(group);
			}

			// Push message to group
			group.messages.push(message);

			return acc;

		}, []);
	}

	scrollToLastMessage(behavior = "smooth") {
		const messageElements = this.bodyElement.querySelectorAll(".msg");
		const lastMessage = messageElements.length > 0 ? messageElements[messageElements.length - 1] : null;

		if (lastMessage) {
			lastMessage.scrollIntoView({behavior});
		}
	}

	readUnreadMessages = debounce(500, () => {
		const messages = this.props.messages.filter(message => !isSent(message) && !isRead(message));

		if (messages.length === 0) {
			return;
		}

		if (typeof this.props.handleRead === "function") {
			this.props.handleRead(messages);
		}
	});

	handleScroll = (e) => {
		const el = e.target;
		const top = !(el.scrollTop > 0);

		if (top && (typeof this.props.handleScrollToTop === "function")) {
			this.props.handleScrollToTop();
		}
	}

	sortMessages(messages) {
		return messages.sort((a, b) => {
			if (moment(a.created_at).isAfter(moment(b.created_at))) return 1;
			if (moment(a.created_at).isSame(moment(b.created_at))) {
				return a.id > b.id ? 1 : -1
			}
			return -1;
		});
	}

	/////////////////////////////////
	// Rendering
	/////////////////////////////////

	renderDateBar(date) {
		return (
			<div className="date-bar">{this.getDateBarContent(date)}</div>
		);
	}

	renderAvatar(member) {
		let url = member.avatar ? member.avatar.url : router.url(this.props.defaultAvatarUrl);

		return (
			<div className="avatar object-fit">
				<img src={url} alt={member.nickname}/>
			</div>
		)
	}

	renderMessages(messages) {
		messages = this.sortMessages(messages);
		return messages.map((message, index) => {
			const className = classList(
				(index === 0) && "first",
				(index > 0) && (index < messages.length - 1) && "mid",
				(index === messages.length - 1) && "last"
			);

			return (
				<Message key={message.id} className={className} handleAction={this.props.handleMessageAction} {...message}/>
			);
		});
	}

	renderContainers(messages) {
		return this.groupMessagesBySender(messages).map((group, index) => {
			const isMyGroup = group.sender.id === userStore.get("id");
			const avatarMarkup = isMyGroup ? null : this.renderAvatar(group.sender);
			const className = classList("msg-container", isMyGroup && "yours");

			return (
				<div key={index} className={className}>
					{avatarMarkup}
					<div className="msg-wrap">{this.renderMessages(group.messages)}</div>
				</div>
			);
		});
	}

	renderGroups() {
		const groups = this.groups;

		return Object.keys(groups).map(group => {
			const messages = groups[group];

			return (
				<div key={group} className="msg-group">
					{this.renderDateBar(moment(messages[0].created_at))}
					{this.renderContainers(messages)}
				</div>
			);
		});
	}

	renderInactive() {
		if (this.props.channel.status === "inactive") {
			return (
				<div className="chat-inactive-msg">
					Denne samtale er deaktiveret fordi der ikke er en aktiv reservation.
				</div>
			);
		}
	}

	render() {
		return (
			<div ref={el => this.bodyElement = el} className={this.className} onScroll={this.handleScroll}>
				{this.renderGroups()}
				{this.renderInactive()}
			</div>
		);
	}

}

module.exports = {
	ChatWindow
};



// WEBPACK FOOTER //
// ./resources/js/components/backend/chat/ChatWindow.js